<template>
    <div class="min-h-screen bg-gray-100">
        <navbar/>
        <div class="py-10">
            <header>
                <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                    <div class="md:flex md:items-center md:justify-between">
                        <div class="flex-1 min-w-0">
                            <h2 class="py-0.5 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
                                {{ $t('Account settings') }}
                            </h2>
                        </div>
                    </div>
                </div>
            </header>
            <main>
                <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                    <div class="mt-10 shadow sm:rounded-lg">
                        <form @submit.prevent="editUser">
                            <loading :status="loading.details"/>
                            <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Personal information') }}</h3>
                                    <p class="mt-1 text-sm leading-5 text-gray-500">
                                        {{ $t('This information will be displayed publicly so be careful what you share') }}.
                                    </p>
                                    <p v-if="isSsoUser" class="mt-2 text-xs leading-4 text-blue-700">
                                        {{ $t('Your account uses GCC single sign-on, so your name and email are managed from GCC') }}.
                                    </p>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <div class="grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                            <div class="mt-1 relative rounded-md shadow-sm">
                                                <input id="name" v-model="user.name" :disabled="isSsoUser" :placeholder="$t('Your name')" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" :class="isSsoUser ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                                            <div class="mt-1 relative rounded-md shadow-sm">
                                                <input id="email" v-model="user.email" :disabled="isSsoUser" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" :class="isSsoUser ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''" placeholder="email@email.com" type="email">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('Role') }}</label>
                                            <div class="mt-1 text-sm text-gray-800">
                                                {{ user.role && user.role.name ? user.role.name : '-' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-4 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('Departments') }}</label>
                                            <div class="mt-2">
                                                <div v-if="!user.departments || user.departments.length < 1" class="text-sm text-gray-500">
                                                    {{ $t('No departments assigned') }}
                                                </div>
                                                <div v-else class="flex flex-wrap gap-2">
                                                    <span v-for="department in user.departments" :key="'dept-' + department.id" class="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-xs text-blue-700">
                                                        {{ department.name }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6">
                                        <label class="block text-sm leading-5 font-medium text-gray-700">{{ $t('Avatar') }}</label>
                                        <div class="mt-2 flex items-center select-none">
                                            <div class="avatar-editor relative inline-block h-12 w-12 overflow-hidden bg-gray-100">
                                                <button
                                                    v-show="!isSsoUser && user.avatar !== 'gravatar'"
                                                    class="avatar-editor-remove absolute top-0 right-0 items-center p-0.5 rounded-full leading-4 bg-red-600 text-white cursor-pointer"
                                                    type="button"
                                                    @click="removeAvatar"
                                                >
                                                    <svg-vue class="h-4 w-4 p-px" icon="font-awesome.times-light"></svg-vue>
                                                </button>
                                                <img
                                                    :src="user.avatar === 'gravatar' ? user.gravatar : user.avatar_preview"
                                                    alt="User avatar"
                                                    class="h-full w-full text-gray-300 rounded-full object-cover"
                                                />
                                            </div>
                                            <span v-if="!isSsoUser" class="ml-5 rounded-md shadow-sm">
                                                <input ref="changeAvatar" accept=".png,.jpg,.jpeg" hidden type="file" @change="changeAvatar($event)">
                                                <button
                                                    class="py-2 px-3 border border-gray-300 rounded-md text-sm leading-4 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
                                                    type="button"
                                                    @click="selectAvatar"
                                                >
                                                    {{ $t('Change') }}
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="!isSsoUser" class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                                <span class="inline-flex">
                                    <button class="btn btn-blue rounded-md shadow-sm" type="submit">
                                        {{ $t('Save') }}
                                    </button>
                                </span>
                            </div>
                        </form>
                    </div>
                    <div class="mt-10 shadow sm:rounded-lg">
                        <loading :status="loading.push"/>
                        <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                            <div class="md:col-span-1">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Browser notifications') }}</h3>
                                <p class="mt-1 text-sm leading-5 text-gray-500">
                                    {{ $t('Enable notifications on this browser to receive ticket updates even when the page is closed') }}.
                                </p>
                            </div>
                            <div class="mt-5 md:mt-0 md:col-span-2">
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div class="rounded-lg border border-gray-200 p-4">
                                        <div class="text-xs uppercase tracking-wide text-gray-500">{{ $t('Status') }}</div>
                                        <div class="mt-2 text-sm font-medium text-gray-900">{{ pushStatusLabel }}</div>
                                        <div class="mt-2 text-xs text-gray-500">{{ pushStatusHint }}</div>
                                    </div>
                                    <div class="rounded-lg border border-gray-200 p-4">
                                        <div class="text-xs uppercase tracking-wide text-gray-500">{{ $t('Token status') }}</div>
                                        <div class="mt-2 text-sm font-medium text-gray-900">
                                            {{ push.token_preview || '-' }}
                                        </div>
                                        <div class="mt-2 text-xs text-gray-500">
                                            {{ push.last_used_at ? $t('Last synced') + ': ' + push.last_used_at : $t('Not connected yet') }}
                                        </div>
                                    </div>
                                    <div class="rounded-lg border border-gray-200 p-4 sm:col-span-2">
                                        <div class="text-xs uppercase tracking-wide text-gray-500">{{ $t('Browser capabilities') }}</div>
                                        <div class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                                            <div>{{ $t('Secure context') }}: <span class="font-medium text-gray-900">{{ pushCapabilities.secureContext ? $t('Yes') : $t('No') }}</span></div>
                                            <div>{{ $t('Service worker') }}: <span class="font-medium text-gray-900">{{ pushCapabilities.serviceWorkerRegistered ? $t('Ready') : $t('Not ready') }}</span></div>
                                            <div>{{ $t('Vibration support') }}: <span class="font-medium text-gray-900">{{ pushCapabilities.supportsVibrate ? $t('Supported') : $t('Not supported') }}</span></div>
                                            <div>{{ $t('App badge support') }}: <span class="font-medium text-gray-900">{{ pushCapabilities.supportsBadging ? $t('Supported') : $t('Not supported') }}</span></div>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-4 flex flex-wrap gap-3">
                                    <button
                                        v-if="canEnablePush"
                                        class="btn btn-blue rounded-md shadow-sm"
                                        type="button"
                                        @click="enablePush"
                                    >
                                        {{ $t('Enable notifications') }}
                                    </button>
                                    <button
                                        v-if="canReconnectPush"
                                        class="btn rounded-md shadow-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                                        type="button"
                                        @click="reconnectPush"
                                    >
                                        {{ $t('Reconnect') }}
                                    </button>
                                    <button
                                        v-if="canDisconnectPush"
                                        class="btn rounded-md shadow-sm border border-red-200 text-red-700 bg-white hover:bg-red-50"
                                        type="button"
                                        @click="disconnectPush"
                                    >
                                        {{ $t('Disconnect this browser') }}
                                    </button>
                                    <button
                                        :class="canTestPush ? 'border-blue-200 text-blue-700 bg-white hover:bg-blue-50' : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'"
                                        :disabled="!canTestPush || loading.pushTest"
                                        class="btn rounded-md shadow-sm border"
                                        type="button"
                                        @click="sendTestPush"
                                    >
                                        {{ $t('Test push notification') }}
                                    </button>
                                </div>
                                <div class="mt-4 text-xs text-gray-500">
                                    {{ $t('If browser notifications are blocked, you must allow them from your browser settings') }}.
                                </div>
                                <div v-if="!canTestPush" class="mt-2 text-xs text-gray-500">
                                    {{ $t('Test push button will be enabled after this browser is connected and notification permission is granted') }}.
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="!isSsoUser" class="mt-10 shadow sm:rounded-lg">
                        <form @submit.prevent="changePassword">
                            <loading :status="loading.password"/>
                            <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Change password') }}</h3>
                                    <p class="mt-1 text-sm leading-5 text-gray-500">
                                        {{ $t('Change your password for a new one, valid for the next login') }}.
                                    </p>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <div class="grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="current_password">{{ $t('Current password') }}</label>
                                            <div class="mt-1 relative rounded-md shadow-sm">
                                                <input
                                                    id="current_password"
                                                    v-model="user.current_password"
                                                    :placeholder="$t('Your current password')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    type="password"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="password">{{ $t('New password') }}</label>
                                            <div class="mt-1 relative rounded-md shadow-sm">
                                                <input
                                                    id="password"
                                                    v-model="user.password"
                                                    :placeholder="$t('Your new password')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    type="password"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="password_confirmation">{{ $t('Current password') }}</label>
                                            <div class="mt-1 relative rounded-md shadow-sm">
                                                <input
                                                    id="password_confirmation"
                                                    v-model="user.password_confirmation"
                                                    :placeholder="$t('Confirm your new password')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    type="password"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                                <span class="inline-flex">
                                    <button class="btn btn-blue rounded-md shadow-sm" type="submit">
                                        {{ $t('Change password') }}
                                    </button>
                                </span>
                            </div>
                        </form>
                    </div>
                    <div v-else class="mt-10 shadow sm:rounded-lg">
                        <div class="bg-white px-4 py-5 sm:p-6 flex items-start gap-3">
                            <svg-vue class="h-5 w-5 mt-0.5 text-blue-500 flex-shrink-0" icon="font-awesome.info-circle-light"></svg-vue>
                            <div>
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Change password') }}</h3>
                                <p class="mt-1 text-sm leading-5 text-gray-500">
                                    {{ $t('Your account uses GCC single sign-on, so your password is managed from GCC') }}.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    </div>
</template>

<script>
import Navbar from "@/components/layout/shared/navbar";
import {getPushCapabilities, getPushStatus, initPushNotifications, requestPushPermission, syncPushToken, unregisterPushToken} from "@/services/push";

export default {
    name: "Account",
    metaInfo() {
        return {
            title: this.$i18n.t('Account')
        }
    },
    components: {Navbar},
    mounted() {
        this.getUser();
        this.refreshPushStatus();
    },
    data() {
        return {
            loading: {
                details: true,
                password: false,
                push: false,
                pushTest: false,
            },
            user: {
                name: null,
                auth_source: 'local',
                avatar: null,
                gravatar: null,
                avatar_preview: null,
                email: null,
                role: null,
                departments: [],
                current_password: null,
                password: null,
                password_confirmation: null,
            },
            push: {
                configured: false,
                supported: false,
                permission: 'default',
                registered: false,
                token_preview: null,
                device_key: null,
                last_used_at: null,
            },
            pushCapabilities: {
                secureContext: false,
                serviceWorkerRegistered: false,
                notificationPermission: 'unsupported',
                supportsVibrate: false,
                supportsBadging: false,
            },
        }
    },
    computed: {
        isSsoUser() {
            // Saat SSO aktif, kredensial semua user dikelola GCC — hanya
            // Admin lokal (fallback darurat login lokal saat gcc down)
            // yang masih boleh mengubah password lokalnya.
            const ssoEnabled = !!(window.app && window.app.gcc_sso);
            const isLocalAdmin = this.user.role_id === 1 && this.user.auth_source === 'local';
            return ssoEnabled && !isLocalAdmin;
        },
        pushStatusLabel() {
            if (!this.push.configured) {
                return this.$t('Push notifications are not configured on the server');
            }
            if (!this.push.supported) {
                return this.$t('This browser does not support push notifications');
            }
            if (this.push.permission === 'denied') {
                return this.$t('Notifications are blocked in this browser');
            }
            if (this.push.permission !== 'granted') {
                return this.$t('Notifications are not enabled yet');
            }
            if (this.push.registered) {
                return this.$t('Connected on this browser');
            }
            return this.$t('Permission granted but token is not connected');
        },
        pushStatusHint() {
            if (!this.push.configured) {
                return this.$t('Fill the Firebase configuration in the application environment first');
            }
            if (!this.push.supported) {
                return this.$t('Try a modern Chromium, Firefox, or Edge browser');
            }
            if (this.push.permission === 'denied') {
                return this.$t('The application cannot show background push notifications until browser permission is allowed');
            }
            if (this.push.permission !== 'granted') {
                return this.$t('Foreground badge updates still work, but background push requires notification permission');
            }
            if (this.push.registered) {
                return this.$t('This device is ready to receive push notifications');
            }
            return this.$t('Use reconnect to generate and save the current browser token again');
        },
        canEnablePush() {
            return this.push.configured && this.push.supported && this.push.permission !== 'granted';
        },
        canReconnectPush() {
            return this.push.configured && this.push.supported && this.push.permission === 'granted';
        },
        canDisconnectPush() {
            return this.push.registered;
        },
        canTestPush() {
            return this.push.configured && this.push.supported && this.push.permission === 'granted' && this.push.registered;
        }
    },
    methods: {
        async refreshPushStatus() {
            this.loading.push = true;
            this.push = await getPushStatus();
            this.pushCapabilities = await getPushCapabilities();
            this.loading.push = false;
        },
        getUser() {
            const self = this;
            self.loading.details = true;
            axios.get('api/auth/user').then(function (response) {
                self.loading.details = false;
                self.user.name = response.data.name;
                self.user.auth_source = response.data.auth_source || 'local';
                self.user.email = response.data.email;
                self.user.avatar = response.data.avatar;
                self.user.gravatar = response.data.gravatar;
                self.user.avatar_preview = response.data.avatar;
                self.user.role = response.data.role || null;
                self.user.departments = response.data.departments || [];
            }).catch(function () {
                self.loading.details = false;
            });
        },
        async enablePush() {
            this.loading.push = true;
            try {
                const granted = await requestPushPermission(this);
                if (granted) {
                    await initPushNotifications(this);
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: this.$i18n.t('Browser notifications enabled successfully').toString(),
                        type: 'success'
                    });
                }
            } catch (error) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('Unable to enable browser notifications').toString(),
                    type: 'error'
                });
            } finally {
                await this.refreshPushStatus();
            }
        },
        async reconnectPush() {
            this.loading.push = true;
            try {
                await syncPushToken(this, true);
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: this.$i18n.t('Push token synchronized successfully').toString(),
                    type: 'success'
                });
            } catch (error) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('Unable to synchronize push token').toString(),
                    type: 'error'
                });
            } finally {
                await this.refreshPushStatus();
            }
        },
        async disconnectPush() {
            this.loading.push = true;
            try {
                await unregisterPushToken();
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: this.$i18n.t('This browser has been disconnected from push notifications').toString(),
                    type: 'success'
                });
            } catch (error) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('Unable to disconnect this browser from push notifications').toString(),
                    type: 'error'
                });
            } finally {
                await this.refreshPushStatus();
            }
        },
        async sendTestPush() {
            this.loading.pushTest = true;
            try {
                const response = await axios.post('api/push/test', {
                    device_key: this.push.device_key,
                    url: window.location.origin + '/account',
                });
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: (response.data && response.data.message ? response.data.message : this.$i18n.t('Test push notification sent')).toString(),
                    type: 'success'
                });
                this.$store.commit('setNotificationUnreadTotal', (this.$store.state.notificationUnreadTotal || 0) + 1);
                window.dispatchEvent(new CustomEvent('dashboard-refresh-notifications'));
            } catch (error) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: (
                        error && error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : this.$i18n.t('Unable to send test push notification')
                    ).toString(),
                    type: 'error'
                });
            } finally {
                this.loading.pushTest = false;
                await this.refreshPushStatus();
            }
        },
        selectAvatar() {
            this.$refs.changeAvatar.click();
        },
        removeAvatar() {
            this.user.avatar = 'gravatar';
            this.user.avatar_preview = null;
            this.$refs.changeAvatar.value = '';
        },
        changeAvatar(event) {
            const self = this;
            if (event.target.files.length) {
                if (['image/png', 'mage/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'].includes(event.target.files[0].type)) {
                    self.user.avatar = event.target.files[0];
                    self.user.avatar_preview = URL.createObjectURL(event.target.files[0]);
                } else {
                    self.$notify({
                        title: self.$i18n.t('Unsupported file type').toString(),
                        text: self.$i18n.t('Only image type files are accepted').toString(),
                        type: 'error'
                    })
                }
            }
        },
        editUser() {
            const self = this;
            const formData = new FormData();
            self.loading.details = true;
            formData.append('name', self.user.name);
            formData.append('email', self.user.email);
            if (self.user.avatar === 'gravatar') {
                formData.append('gravatar', 'true');
            } else if (self.user.avatar instanceof File) {
                formData.append('avatar', self.user.avatar);
            }
            axios.post('api/account/update', formData, {headers: {'Content-Type': 'multipart/form-data'}}).then(function (response) {
                self.loading.details = false;
                self.user.avatar = response.data.avatar;
                self.user.gravatar = response.data.gravatar;
                self.user.avatar_preview = response.data.avatar;
                self.user.role = response.data.role || self.user.role;
                self.user.departments = response.data.departments || self.user.departments;
                self.$refs.changeAvatar.value = '';
                self.$store.commit('updateUser', response.data);
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Your account details have been updated').toString(),
                    type: 'success'
                })
            }).catch(function () {
                self.loading.details = false;
            });
        },
        changePassword() {
            const self = this;
            self.loading.password = true;
            axios.post('api/account/password', {
                current_password: self.user.current_password,
                password: self.user.password,
                password_confirmation: self.user.password_confirmation,
            }).then(function () {
                self.loading.password = false;
                self.user.current_password = null;
                self.user.password = null;
                self.user.password_confirmation = null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Password changed successfully').toString(),
                    type: 'success'
                })
            }).catch(function () {
                self.loading.password = false;
            });
        }
    }
}
</script>

<style lang="scss">
.avatar-editor {
    .avatar-editor-remove {
        @apply hidden;
    }

    &:hover {
        .avatar-editor-remove {
            @apply inline-flex;
        }
    }
}
</style>

